import {
  LogTemplateType,
  TemplateTypeEnum,
  useAllWorklogsTemplatesForKpiTrackerLazyQuery,
  useAssemblyTemplatesLazyQuery,
  LogsTemplate,
} from '@/__generated__/graphql';
import * as kpiState from '@/modules/KpiTracker/state';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

type Props = {
  structureIds: string[];
};

export const useFetchWorklogTemplatesEffect = ({ structureIds }: Props) => {
  const [fetchWorklogTemplates] = useAllWorklogsTemplatesForKpiTrackerLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const [fetchAssemblyTemplates] = useAssemblyTemplatesLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const setSelectedWorklogsTemplate = useSetAtom(kpiState.selectedWorklogsTemplate);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        if (!Array.isArray(structureIds) || structureIds.length === 0) return;

        const firstStructureId = structureIds[0];
        const secondStructureId = structureIds?.[1];

        const firstTemplateId = await (async () => {
          try {
            const firstAssemblyTemplates = await fetchAssemblyTemplates({
              variables: {
                structureId: firstStructureId,
                type: TemplateTypeEnum.Assembly,
              },
            });

            return firstAssemblyTemplates?.data?.allTemplates?.at(0)?.id;
          } catch {
            return undefined;
          }
        })();

        const firstWorkpack = await (async () => {
          if (!firstTemplateId) return;
          const worklogTemplates = await fetchWorklogTemplates({
            variables: {
              input: {
                structureId: firstStructureId,
                type: LogTemplateType.WorklogTemplate,
              },
            },
          });

          return worklogTemplates?.data?.allLogsTemplates?.at(0);
        })();

        if (firstWorkpack) {
          setSelectedWorklogsTemplate(firstWorkpack as LogsTemplate);
          return;
        }

        const secondTemplateId = await (async () => {
          try {
            const secondAssemblyTemplates = await fetchAssemblyTemplates({
              variables: {
                structureId: secondStructureId,
                type: TemplateTypeEnum.Assembly,
              },
            });

            return secondAssemblyTemplates?.data?.allTemplates?.at(0)?.id || firstTemplateId;
          } catch {
            return undefined;
          }
        })();

        if (!secondTemplateId) return;

        const secondWorkpack = await (async () => {
          const worklogTemplates = await fetchWorklogTemplates({
            variables: {
              input: {
                structureId: secondStructureId,
                type: LogTemplateType.WorklogTemplate,
              },
            },
          });

          return worklogTemplates?.data?.allLogsTemplates?.at(0);
        })();

        if (secondWorkpack) {
          setSelectedWorklogsTemplate(secondWorkpack as LogsTemplate);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetch().then();
  }, [fetchAssemblyTemplates, fetchWorklogTemplates, setSelectedWorklogsTemplate, structureIds]);

  return { loading };
};
